import { z } from 'zod';
import { EventConfigSchema, LocationConfigSchema, type LocationConfig } from './config.ts';
import { OrganizationPublicSchema } from './organization.ts';

/**
 * The published configuration for one event, as stored in KV.
 *
 * This is the contract between the admin publisher and the public hot path.
 * It is validated on read as well as on write: a snapshot is the only thing
 * standing between a bad admin edit and every participant's check-in page,
 * and KV is eventually consistent, so a half-written or hand-edited value has
 * to fail loudly rather than render.
 *
 * `snapshot_version` describes this envelope, not the event's
 * `config_version`. Bump it only when the envelope changes incompatibly.
 */
export const EventSnapshotSchema = z.strictObject({
  snapshot_version: z.literal(1),
  published_at: z.iso.datetime({ offset: true }),
  event: EventConfigSchema,
  /** May be empty: an event with a single implicit venue has no real ones. */
  locations: z.array(LocationConfigSchema),
  organization: OrganizationPublicSchema.nullable(),
});

export type EventSnapshot = z.infer<typeof EventSnapshotSchema>;

/**
 * Active, real locations in display order — what a participant may choose.
 *
 * The placeholder is excluded here and nowhere else: it exists to satisfy the
 * foreign key on `checkins.location_id`, not to be offered as an answer.
 */
export function selectableLocations(snapshot: EventSnapshot): LocationConfig[] {
  return snapshot.locations
    .filter((location) => location.is_active && !location.is_placeholder)
    .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name, 'vi'));
}

/**
 * The location to record when a participant was never asked to choose one.
 *
 * Returns undefined for an event that has real locations; the caller must
 * then require an explicit choice.
 */
export function implicitLocation(snapshot: EventSnapshot): LocationConfig | undefined {
  if (selectableLocations(snapshot).length > 0) return undefined;
  return snapshot.locations.find((location) => location.is_placeholder && location.is_active);
}
